/* UI Store - Pinia (Toasts, Modals, Loading, etc.) */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ToastMessage } from '@/types'

export const useUiStore = defineStore('ui', () => {
  const toasts = ref<ToastMessage[]>([])
  const modals = ref<Record<string, boolean>>({})
  const sidebarOpen = ref(false)
  const sidebarCollapsed = ref(false)
  const globalLoading = ref(false)
  const pageTitle = ref('Jardinera')

  const toastCount = computed(() => toasts.value.length)

  function showToast(
    type: ToastMessage['type'],
    title: string,
    message?: string,
    duration = 4000
  ) {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const toast: ToastMessage = { id, type, title, message, duration }
    toasts.value.push(toast)
    
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration)
    }
    
    return id
  }

  function removeToast(id: string) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  function clearToasts() {
    toasts.value = []
  }

  function openModal(name: string) {
    modals.value[name] = true
    document.body.style.overflow = 'hidden'
  }

  function closeModal(name: string) {
    modals.value[name] = false
    if (!Object.values(modals.value).some(v => v)) {
      document.body.style.overflow = ''
    }
  }

  function toggleModal(name: string) {
    if (modals.value[name]) {
      closeModal(name)
    } else {
      openModal(name)
    }
  }

  function closeAllModals() {
    Object.keys(modals.value).forEach(key => {
      modals.value[key] = false
    })
    document.body.style.overflow = ''
  }

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value
  }

  function setSidebarOpen(open: boolean) {
    sidebarOpen.value = open
  }

  function toggleSidebarCollapsed() {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  function setGlobalLoading(loading: boolean) {
    globalLoading.value = loading
  }

  function setPageTitle(title: string) {
    pageTitle.value = title
    document.title = `${title} - Jardinera`
  }

  // Convenience toast methods
  const toast = {
    success: (title: string, message?: string, duration?: number) => 
      showToast('success', title, message, duration),
    error: (title: string, message?: string, duration?: number) => 
      showToast('error', title, message, duration),
    warning: (title: string, message?: string, duration?: number) => 
      showToast('warning', title, message, duration),
    info: (title: string, message?: string, duration?: number) => 
      showToast('info', title, message, duration),
  }

  return {
    // State
    toasts,
    modals,
    sidebarOpen,
    sidebarCollapsed,
    globalLoading,
    pageTitle,
    // Getters
    toastCount,
    // Actions
    showToast,
    removeToast,
    clearToasts,
    openModal,
    closeModal,
    toggleModal,
    closeAllModals,
    toggleSidebar,
    setSidebarOpen,
    toggleSidebarCollapsed,
    setGlobalLoading,
    setPageTitle,
    toast,
  }
})